import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Pencil, Plus, Archive } from "lucide-react";
import { personsApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import type { PersonListItemDto } from "@/types/counterparties";
import { COUNTERPARTY_PERMISSIONS } from "@/types/counterparties";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { RowActions, type RowAction } from "@/components/ux";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { CounterpartyStatusBadge } from "@/components/counterparties/StatusBadge";
import { ActiveDealsBlockedDialog } from "@/components/counterparties/ActiveDealsBlockedDialog";
import { useDateFormatter } from "@/utils/datetime";
import { invalidateCounterpartyReads, personKeys } from "../counterpartyKeys";

const PERMS = COUNTERPARTY_PERMISSIONS.Person;
const PAGE_SIZE = 25;

export function PersonListPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const navigate = useNavigate();
  const { date } = useDateFormatter();

  const canCreate = hasPermission(PERMS.Create);
  const canUpdate = hasPermission(PERMS.Update);
  const canDelete = hasPermission(PERMS.Delete);

  const [search, setSearch] = useState("");
  const [archiveScope, setArchiveScope] = useState<"Active" | "All" | "Archived">("Active");
  const [blockedFor, setBlockedFor] = useState<PersonListItemDto | null>(null);
  const [page, setPage] = useState(1);

  useBreadcrumbs([{ label: "Persons" }]);

  useEffect(() => {
    setPage(1);
  }, [search, archiveScope]);

  const personsQuery = useQuery({
    queryKey: personKeys.list({ search, archiveScope, page }),
    queryFn: () =>
      personsApi.list({
        q: search || undefined,
        includeArchived: archiveScope === "All",
        archivedOnly: archiveScope === "Archived",
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const totalPages = useMemo(() => {
    const data = personsQuery.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [personsQuery.data]);

  const archive = useMutation({
    mutationFn: (id: string) => personsApi.archive(id),
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "person");
      toast.success("Person archived.");
    },
    onError: (e, id) => {
      if (e instanceof ApiError && e.status === 409) {
        const row = (personsQuery.data?.data ?? []).find((p) => p.id === id);
        if (row) setBlockedFor(row);
      } else {
        toast.error(e instanceof Error ? e.message : "Archive failed.");
      }
    },
  });

  const rows = personsQuery.data?.data ?? [];

  const columns: Column<PersonListItemDto>[] = [
    {
      key: "name",
      header: "Name",
      render: (p) => (
        <Link to={`/counterparties/persons/${p.id}`} className="font-semibold hover:text-gold-shadow">
          {p.displayName}
        </Link>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (p) =>
        displayEmail(p.primaryEmail) ??
        (p.primaryEmail ? (
          <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>
        ) : (
          "—"
        )),
    },
    { key: "phone", header: "Phone", render: (p) => p.primaryPhone ?? "—" },
    { key: "state", header: "State", render: (p) => p.state ?? "—" },
    {
      key: "status",
      header: "Status",
      render: (p) => <CounterpartyStatusBadge status={p.status} />,
    },
    {
      key: "created",
      header: "Created",
      render: (p) => date(p.createdAt),
    },
    // No "Role" column. A person has no role: PersonListItemDto and
    // PersonDetailDto carry no such member, and the concept this column was
    // holding a place for -- a person's role on a deal, via CounterpartyDealLink
    // -- was retired by the counterparty sunset, so the promise it made
    // ("Available when deals data ships in PR 3") is unfulfillable rather than
    // merely unfulfilled. Per VendorsListTable.tsx and Table & Tile Standard
    // section 6, a column that can only ever show a placeholder is worse than
    // no column. Do not re-add it.
    {
      key: "actions",
      header: "",
      className: "w-10 text-right",
      render: (p) => (
        <RowActions
          actions={[
            { key: "view", label: "View", icon: Eye, href: `/counterparties/persons/${p.id}` },
            {
              key: "edit",
              label: "Edit",
              icon: Pencil,
              hidden: !canUpdate,
              href: `/counterparties/persons/${p.id}/edit`,
            },
            {
              key: "archive",
              label: "Archive",
              icon: Archive,
              hidden: !canDelete || p.status === "Archived",
              destructive: true,
              onClick: async () => {
                const ok = await dialogs.confirm({
                  title: `Archive ${p.displayName}?`,
                  message:
                    "This counterparty will no longer appear in active lists. Existing deal references are preserved.",
                  confirmLabel: "Archive",
                  destructive: true,
                });
                if (!ok) return;
                archive.mutate(p.id);
              },
            },
          ] satisfies RowAction[]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Counterparties"
        title="Persons"
        actions={
          canCreate && (
            <Button size="sm" onClick={() => navigate("/counterparties/persons/new")}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New person
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search name, email, phone, state…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="grow"
          aria-label="Search persons"
        />
        <Select
          value={archiveScope}
          onChange={(e) => setArchiveScope(e.target.value as typeof archiveScope)}
          className="max-w-[10rem] shrink-0"
          aria-label="Archive scope"
        >
          <option value="Active">Active</option>
          <option value="All">All</option>
          <option value="Archived">Archived</option>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={personsQuery.isLoading}
        error={personsQuery.isError ? (personsQuery.error as Error).message : null}
        emptyMessage={
          canCreate
            ? "No persons match your filters. Click “New person” to add one."
            : "No persons match your filters."
        }
        rowKey={(p) => p.id}
      />

      {personsQuery.data && personsQuery.data.totalCount > 0 && (
        <Pagination
          page={personsQuery.data.page}
          pageSize={personsQuery.data.pageSize}
          totalCount={personsQuery.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}

      {blockedFor && (
        <ActiveDealsBlockedDialog
          open
          displayName={blockedFor.displayName}
          onClose={() => setBlockedFor(null)}
        />
      )}
    </>
  );
}
